import * as Sentry from '@sentry/react';
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, Wifi, WifiOff, AlertTriangle, Info, Check, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { notificationsAPI } from '../lib/api';
import { cn } from '../lib/utils';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      if (error?.response?.status !== 401) {
        Sentry.captureException(error);
      }
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.list({ limit: 20, unread_only: false });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      if (error?.response?.status !== 401) {
        Sentry.captureException(error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for unread count every 60 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch notifications when popover opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Dismiss notification
  const handleDismiss = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update unread count if needed
      const dismissed = notifications.find(n => n.id === notificationId);
      if (dismissed && !dismissed.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Navigate to notification link
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      setOpen(false);
      navigate(notification.link);
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'kpax_disconnected':
        return <WifiOff className="w-4 h-4 text-red-500" />;
      case 'kpax_low_supply':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'system_alert':
        return <Info className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 text-white/70 hover:text-white hover:bg-white/10"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span 
              className="absolute top-0.5 right-0.5 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-red-500 rounded-full"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-96 p-0" 
        align="end" 
        sideOffset={8}
        data-testid="notification-popover"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
                {unreadCount} nouvelles
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary hover:text-primary hover:bg-primary/5 h-7"
              onClick={handleMarkAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <Check className="w-3 h-3 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Wifi className="w-10 h-10 mb-3 text-gray-300" />
              <p className="text-sm font-medium">Aucune notification</p>
              <p className="text-xs text-gray-400 mt-1">Les alertes apparaîtront ici</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'relative px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50',
                    !notification.is_read && 'bg-primary/5/50'
                  )}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        notification.type === 'kpax_disconnected' && 'bg-red-100',
                        notification.type === 'kpax_low_supply' && 'bg-amber-100',
                        notification.type === 'system_alert' && 'bg-blue-100'
                      )}>
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          'text-sm',
                          !notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'
                        )}>
                          {notification.title}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-gray-400 hover:text-gray-600 -mt-0.5 -mr-1"
                          onClick={(e) => handleDismiss(e, notification.id)}
                          data-testid={`dismiss-notification-${notification.id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(notification.created_at)}
                        </span>
                        {notification.priority && notification.priority !== 'low' && (
                          <Badge 
                            variant="outline" 
                            className={cn('text-[10px] px-1.5 py-0', getPriorityColor(notification.priority))}
                          >
                            {notification.priority === 'critical' ? 'Critique' : 
                             notification.priority === 'high' ? 'Haute' : 'Moyenne'}
                          </Badge>
                        )}
                        {notification.link && (
                          <span className="flex items-center text-xs text-primary">
                            <ExternalLink className="w-3 h-3 mr-0.5" />
                            Voir
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {!notification.is_read && (
                      <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary/50" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              Affichage des 20 dernières notifications
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
